import { useAtomValue } from 'jotai';
import T from '@wojtekmaj/react-t';

import {
  Amount,
  CurrentPriceAmount,
  Icon,
  InputWrapper,
  Label,
  Name,
  NeedMore,
  Places,
  Price,
  RegularPriceAmount,
} from './plan_buttons.styles';

import ContactUsLink from '../../../contact_us_link';

import { formatPrice } from '../../../../utils/helpers';

import { plansState } from '../../../../store';

import checkIcon from '../../../../static/check.svg';

import type { Plan } from '@rewardopl/types';

const sortByMaxPlaces = (a: Plan, b: Plan) => (a.max_places || 0) - (b.max_places || 0);

function shouldUsePromoPrice(plan: Plan): plan is Plan & { promo_price: number } {
  return Boolean(
    plan.promo_price && plan.promo_valid_to && new Date(plan.promo_valid_to) >= new Date(),
  );
}

function getPrice(plan: Plan): number {
  if (shouldUsePromoPrice(plan)) {
    return plan.promo_price;
  }

  return plan.price;
}

type PlanButtonProps = {
  checked: boolean;
  name: string;
  onChange: (planId: Plan['_id']) => void;
  plan: Plan;
};

function PlanButton({ checked, name, onChange, plan }: PlanButtonProps) {
  return (
    <Label key={plan._id} checked={checked}>
      <Icon>{checked ? <img width={22} height={22} src={checkIcon} alt="" /> : null}</Icon>
      <input
        checked={checked}
        name={name}
        onChange={() => onChange(plan._id)}
        required
        type="radio"
        value={plan._id}
      />
      <Name>{plan.name}</Name>
      <Places>
        <Amount>{plan.max_places}</Amount> <T>{plan.max_places !== 1 ? 'places' : 'place'}</T>
      </Places>
      <Price>
        <CurrentPriceAmount>{formatPrice(getPrice(plan), plan.currency)}</CurrentPriceAmount>
        {shouldUsePromoPrice(plan) && (
          <RegularPriceAmount>{formatPrice(plan.price, plan.currency)}</RegularPriceAmount>
        )}
        <T>monthly</T>
      </Price>
    </Label>
  );
}

type PlanButtonsProps = {
  name: string;
  onChange: (planId: Plan['_id']) => void;
  value: string;
};

export default function PlanButtons({ name, onChange, value }: PlanButtonsProps) {
  const plans = useAtomValue(plansState);

  const plansSortedByMaxPlaces = [...plans].sort(sortByMaxPlaces);

  const lastPlan = plansSortedByMaxPlaces[plansSortedByMaxPlaces.length - 1];

  if (!lastPlan) {
    throw new Error('No plans found');
  }

  return (
    <InputWrapper>
      {plans.map((plan) => (
        <PlanButton
          key={plan._id}
          checked={value === plan._id}
          name={name}
          onChange={onChange}
          plan={plan}
        />
      ))}
      <NeedMore>
        <p>
          <T amount={lastPlan.max_places}>
            {'Have more than {amount} places or looking for a dedicated app?'}
          </T>
        </p>
        <p>
          <ContactUsLink />
        </p>
      </NeedMore>
    </InputWrapper>
  );
}
